import { useCallback, useEffect, useRef, useState } from 'react';
import type { CreatorsService } from '@domain/usecases/creators/CreatorsService';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { CreatorSortField } from '@domain/usecases/creators/CreatorSortField';
import { useSort } from '@presentation/hooks/useSort';

export const useGetCreators = (firstPage: any, creatorsService: CreatorsService) => {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();

	const { sort, handleSort } = useSort<CreatorSortField>(CreatorSortField.puzzles, 'desc');

	// Data
	const [creators, setCreators] = useState<any[]>(firstPage.items || []);
	const [count, setCount] = useState<number>(firstPage.count);
	const last = useRef<string | undefined>(firstPage.items[firstPage.items.length - 1]?.id);
	const [hasMore, setHasMore] = useState(firstPage.hasMore);
	const isInProgress = useRef<boolean>(false);

	// Handlers
	const fetchContent = useCallback(
		async (hardReset?: boolean) => {
			if (isInProgress.current) {
				return;
			}
			isInProgress.current = true;

			const lastId = !hardReset ? last.current : undefined;

			try {
				const res = await creatorsService.getNextCreatorsPage({
					last: lastId,
					order: sort,
				});

				setCreators((prevState) => {
					if (hardReset) {
						return [...res.items];
					}

					return [...prevState].concat([...res.items]);
				});
				setCount(res.count);

				if (res.items.length) {
					last.current = res.items[res.items.length - 1].id;
				}
				setHasMore(res.hasMore);
			} catch (e) {
				enqueueSnackbar(t('screens.creators.errors.fetchDataError'), { variant: 'error' });
			} finally {
				isInProgress.current = false;
			}
		},
		[sort, enqueueSnackbar, t, creatorsService]
	);

	// Hooks
	useEffect(() => {
		// Fetch on init
		fetchContent(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sort]);

	return {
		creators,
		hasMore,
		handleSort,
		sort,
		fetchContent,
		isLoading: isInProgress.current,
		count,
	};
};
